import classNames from "classnames";
import { Fragment } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  Bars3Icon,
  ChevronDownIcon
} from "@heroicons/react/24/outline";

import { NavbarLink } from "../../../../../src/shared/layout/navbar/interfaces";
import NavbarMenuButton, {
  type NavbarMenuItem
} from "../../../../../src/shared/layout/navbar/NavbarMenuButton";
import { initializeName } from "../../../../../src/shared/utils/Common";
import GetAuthContext from "../../../../../src/shared/context/AuthContext";
import { whiteLabelStyling } from "../../../../../src/shared/utils/Global";
import Divider from "../../../../../src/components/atomic/Divider";
import GetMixpanelContext from "../../../../../src/shared/context/MixpanelContext";
import {
  ACTIONS_LIST,
  CREDIT_LINE,
  ORG_IDS,
  OVERDRAFT,
  PAGES_LIST
} from "../../../../../src/shared/utils/constants";
import PrivateComponent from "../../../../../src/components/composed/PrivateComponent";
import { useIsMobile } from "../../../shared/hooks/useIsMobile";
import { useMflBlVisibility } from "../../../shared/context/MflBlVisibilityContext";

export default function MainNavbar() {
  const whiteLabelStyle = whiteLabelStyling(window.location?.host);
  const { userState, logout, getPageConfig } = GetAuthContext();
  const { permissions, organizationID } = userState;
  const pageConfig = getPageConfig();
  const { resetIdentity } = GetMixpanelContext();
  const navigate = useNavigate();
  const { isAuthenticated } = userState || {};
  const { isMflBL, isMyQueueVisible } = useMflBlVisibility();

  // Calculate visibility for My Queue and Leads based on MFL BL state
  const isNonMflBL = !isMflBL;
  const showButtonsForMflBL = isMflBL && isMyQueueVisible;

  const isMFL = organizationID === ORG_IDS.MFL;
  const isMFL_LAP = organizationID === ORG_IDS.MFL_LAP;
  const isPFL = organizationID === ORG_IDS.PFL;

  // For non-MFL BL, use permission-based visibility, otherwise show buttons only if isMyQueueVisible is true
  const shouldShowMyQueue =
    ((isMFL_LAP || isPFL || isMFL) &&
      !!permissions?.[PAGES_LIST.MY_QUEUE_PAGE]) ||
    showButtonsForMflBL;

  const shouldShowLeads =
    !isMFL_LAP && !!permissions?.[PAGES_LIST.LEADS_LISTING_PAGE];

  const defaultLandingPage = shouldShowMyQueue ? "/my-queue" : "/leads";
  // console.log(shouldShowMyQueue, showButtonsForMflBL, 'isMFL BL', isMflBL, 'is non mfl bl', isNonMflBL, (isNonMflBL && !!permissions?.[PAGES_LIST.MY_QUEUE_PAGE]))

  const menuDropdown: Array<NavbarLink> = [
    {
      label: "My Profile",
      href: "/user-profile/details",
      showInMobile: true,
      isVisible: !!permissions?.[PAGES_LIST.USER_PROFILE_PAGE]
    },
    {
      label: "Utilities",
      href: "/utilities",
      showInMobile: true,
      isVisible: true
    },
    {
      label: "Integrations",
      href: "/integrations",
      showInMobile: false,
      isVisible: !!permissions?.[PAGES_LIST.INTEGRATIONS_PAGE]
    },
    {
      label: "Raise Ticket",
      onClick: () => {
        window.open("https://finboxaid.freshdesk.com/support/tickets");
      },
      showInMobile: false,
      isVisible: true
    },
    {
      label: "Contact Us",
      onClick: () => {
        window.open("https://finbox.in/contact-us/", "_blank");
      },
      showInMobile: false,
      isVisible: true
    }
  ];

  const productMenuLinks: NavbarMenuItem[] = [
    {
      label: pageConfig["global"].navbarProducts.LOAN_APPLICATION.name,
      href: "/loan-applications",
      showInMobile: true,
      isVisible: !!permissions?.[PAGES_LIST.LOAN_APPLICATIONS_LISTING_PAGE]
    },
    {
      label: "Credit Line",
      isExpandable: true,
      expandDirection: "right",
      showInMobile: false,
      isVisible:
        (!!permissions?.[PAGES_LIST.ACTIVELINE_LISTING_PAGE] ||
          !!permissions?.[PAGES_LIST.ALL_TRANSACTIONS_LISTING_PAGE]) &&
        (pageConfig["global"]?.productTypes
          ? pageConfig["global"]?.productTypes?.includes(CREDIT_LINE)
          : true),
      menuItems: [
        {
          label: "All Transactions",
          href: "/products/all-transactions",
          showInMobile: false,
          isVisible: !!permissions?.[PAGES_LIST.ALL_TRANSACTIONS_LISTING_PAGE]
        },
        {
          label: "Active Lines",
          href: "/products/active-line",
          showInMobile: false,
          isVisible: !!permissions?.[PAGES_LIST.ACTIVELINE_LISTING_PAGE]
        }
      ]
    },
    {
      label: "Overdraft Bills",
      href: "/products/overdraft",
      showInMobile: false,
      isVisible:
        !!permissions?.[PAGES_LIST.OVERDRAFT_LISTING_PAGE] &&
        (pageConfig["global"]?.productTypes
          ? pageConfig["global"]?.productTypes?.includes(OVERDRAFT)
          : true)
    }
  ];

  const navigationLeftLinks: Array<NavbarLink> = [
    {
      label: "My Queue",
      href: "/my-queue",
      showInMobile: false,
      isVisible: shouldShowMyQueue
    },
    {
      label: "Leads",
      href: "/leads",
      showInMobile: false,
      isVisible: shouldShowLeads
    }
  ];

  const isMobileScreen = useIsMobile(640);

  return (
    <Disclosure
      as="nav"
      className={classNames(
        "bg-gray-800 sticky top-0 z-[30] w-full md:relative md:z-auto",
        whiteLabelStyle?.theme?.color?.bg
      )}
    >
      {({ open }) => (
        <>
          <div className="mx-auto max-w-screen-3xl px-4 sm:px-22 lg:px-14">
            <div className="flex h-14 justify-between">
              <div className="flex w-full justify-between">
                <div
                  className={classNames(
                    "flex items-center",
                    !isAuthenticated && whiteLabelStyle?.navbar?.poweredBy
                      ? "justify-between w-full"
                      : "justify-start"
                  )}
                >
                  <div
                    className="flex flex-shrink-0 items-center justify-start"
                    style={{
                      width: `${
                        isMobileScreen
                          ? "auto"
                          : (window.innerWidth - 112) * 0.2
                      }px`
                    }}
                  >
                    <img
                      onClick={() => {
                        const redirectUrl =
                          window.innerWidth < 640 && !isMFL_LAP
                            ? "/home"
                            : defaultLandingPage;

                        navigate(redirectUrl);
                      }}
                      className={classNames(
                        "h-6 w-auto cursor-pointer",
                        whiteLabelStyle?.navbar?.logo?.className
                      )}
                      src={
                        whiteLabelStyle?.navbar?.logo?.img ||
                        "https://cdn-ind-01.finbox.in/finbox-logos/logo-light.svg"
                      }
                      alt="FinBox"
                    />
                  </div>

                  {userState.isAuthenticated && (
                    <div className="hidden sm:ml-4 sm:flex h-full items-center justify-start md:space-x-16">
                      {!isMFL_LAP &&
                        navigationLeftLinks.map((link, index) => {
                          return (
                            link.isVisible && (
                              <NavLink
                                key={index}
                                to={link.href!}
                                className={({ isActive }) =>
                                  classNames(
                                    "block text-center align-middle text-sm font-medium h-min py-2 px-4 rounded-lg",
                                    isActive
                                      ? `${
                                          whiteLabelStyle?.theme?.color
                                            ?.darkbg || "bg-gray-900"
                                        }  text-white`
                                      : `${
                                          whiteLabelStyle?.theme?.color?.bg ||
                                          "bg-gray-800"
                                        } text-white`
                                  )
                                }
                              >
                                {link.label}
                              </NavLink>
                            )
                          );
                        })}

                      <PrivateComponent
                        requiredPage={PAGES_LIST.LEADS_LISTING_PAGE}
                        sourceEntityId={
                          Object.keys(
                            userState?.permissions?.[
                              PAGES_LIST.LEADS_LISTING_PAGE
                            ] ?? {}
                          )?.[0]
                        }
                        requiredFunctions={[ACTIONS_LIST.HIDE_FILTERS]}
                        hideIfRequiredFunctionsPresent
                      >
                        <>
                          {productMenuLinks.filter(
                            (productMenu) => productMenu.isVisible
                          )?.length > 0 ? (
                            <div className="hidden md:flex md:space-x-12 items-center">
                              <NavbarMenuButton
                                buttonText={
                                  <div className="inline-flex items-center justify-between text-white font-medium">
                                    Products
                                    <ChevronDownIcon className="w-4 h-4 text-white ml-2" />
                                  </div>
                                }
                                menuItems={productMenuLinks}
                                preContent={""}
                                styles={{
                                  container: "ml-0 mr-0",
                                  button: "px-0",
                                  menuItem: "text-sm",
                                  menuItemsList: "w-56"
                                }}
                              />
                            </div>
                          ) : null}
                        </>
                      </PrivateComponent>

                      {permissions?.[PAGES_LIST.PARTNERS_PAGE] && (
                        <NavLink
                          to={"/partners/list"}
                          className={({ isActive }) =>
                            classNames(
                              "block text-center align-middle text-sm font-medium h-min py-2 px-4 rounded-lg",
                              isActive
                                ? `${
                                    whiteLabelStyle?.theme?.color?.darkbg ||
                                    "bg-gray-900"
                                  }  text-white`
                                : `${
                                    whiteLabelStyle?.theme?.color?.bg ||
                                    "bg-gray-800"
                                  } text-white`
                            )
                          }
                        >
                          Partners
                        </NavLink>
                      )}

                      {permissions?.[PAGES_LIST.REPORTS_PAGE] && (
                        <NavLink
                          to={"/reports"}
                          className={({ isActive }) =>
                            classNames(
                              "block text-center align-middle text-sm font-medium h-min py-2 px-4 rounded-lg",
                              isActive
                                ? `${
                                    whiteLabelStyle?.theme?.color?.darkbg ||
                                    "bg-gray-900"
                                  }  text-white`
                                : `${
                                    whiteLabelStyle?.theme?.color?.bg ||
                                    "bg-gray-800"
                                  } text-white`
                            )
                          }
                        >
                          Reports
                        </NavLink>
                      )}

                      {permissions?.[PAGES_LIST.ANALYTICS_PAGE] && (
                        <NavLink
                          to={"/analytics"}
                          className={({ isActive }) =>
                            classNames(
                              "block text-center align-middle text-sm font-medium h-min py-2 px-4 rounded-lg",
                              isActive
                                ? `${
                                    whiteLabelStyle?.theme?.color?.darkbg ||
                                    "bg-gray-900"
                                  }  text-white`
                                : `${
                                    whiteLabelStyle?.theme?.color?.bg ||
                                    "bg-gray-800"
                                  } text-white`
                            )
                          }
                        >
                          Analytics
                        </NavLink>
                      )}

                      {permissions?.[PAGES_LIST.ADMIN_PAGE] && (
                        <NavLink
                          to={"/admin/user"}
                          className={({ isActive }) =>
                            classNames(
                              "block text-center align-middle text-sm font-medium h-min py-2 px-4 rounded-lg",
                              isActive
                                ? `${
                                    whiteLabelStyle?.theme?.color?.darkbg ||
                                    "bg-gray-900"
                                  }  text-white`
                                : `${
                                    whiteLabelStyle?.theme?.color?.bg ||
                                    "bg-gray-800"
                                  } text-white`
                            )
                          }
                        >
                          Admin
                        </NavLink>
                      )}
                    </div>
                  )}

                  {!isAuthenticated && whiteLabelStyle?.navbar?.poweredBy ? (
                    <div className="flex items-center">
                      <div className="mr-2 text-xs text-white font-normal">
                        Powered by
                      </div>
                      <div className="h-4 w-16">
                        <img
                          src="https://cdn-ind-01.finbox.in/finbox-logos/logo-light.svg"
                          alt="h-auto w-auto"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>

                {userState.isAuthenticated && (
                  <div className="hidden sm:ml-6 sm:flex sm:items-center border-green-500">
                    {/* Profile dropdown */}
                    <Menu as="div" className="relative ml-3">
                      <div>
                        <Menu.Button
                          className={classNames(
                            "flex rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 p-0.5 bg-indigo-600",
                            whiteLabelStyle?.theme?.color?.darkbg
                          )}
                        >
                          <span className="sr-only">Open user menu</span>
                          <div
                            className={classNames(
                              "h-8 w-8 rounded-full text-white bg-indigo-600 flex items-center justify-center font-medium border border-indigo-600",
                              whiteLabelStyle?.theme?.color?.darkbg,
                              whiteLabelStyle?.theme?.color?.borderDark
                            )}
                          >
                            {userState?.name && initializeName(userState.name)}
                          </div>
                        </Menu.Button>
                      </div>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-[30] mt-2 min-w-[220px] origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {userState.isAuthenticated &&
                            window.location.pathname !== "/maintenance" && (
                              <div className="flex items-start px-4 py-2 mb-2">
                                <div className="flex-shrink-0">
                                  <div
                                    className={classNames(
                                      "h-6 w-6 rounded-full text-white bg-indigo-600 flex items-center justify-center text-xs font-medium",
                                      whiteLabelStyle?.theme?.color?.darkbg,
                                      whiteLabelStyle?.theme?.color?.borderDark
                                    )}
                                  >
                                    {userState?.name &&
                                      initializeName(userState.name)}
                                  </div>
                                </div>
                                <div className="ml-2">
                                  <div
                                    className="text-sm text-gray-700 font-medium mb-1"
                                    title={userState?.name}
                                  >
                                    {userState?.name}
                                  </div>
                                  <div
                                    className="text-xs text-gray-500 truncate max-w-[150px]"
                                    title={userState?.email}
                                  >
                                    {userState?.email}
                                  </div>
                                </div>
                              </div>
                            )}

                          <Divider className="mb-2" />

                          {menuDropdown.map((menuItem, index) => {
                            return menuItem.isVisible ? (
                              <Menu.Item key={index}>
                                {menuItem.href ? (
                                  <NavLink
                                    to={menuItem?.href}
                                    className={classNames(
                                      "px-4 py-2 text-sm text-gray-700 flex items-center justify-start hover:bg-indigo-50",
                                      whiteLabelStyle?.theme?.hover?.bg
                                    )}
                                    onClick={menuItem?.onClick}
                                  >
                                    {menuItem.icon ?? null}
                                    {menuItem.label}
                                  </NavLink>
                                ) : (
                                  <a
                                    className={classNames(
                                      "px-4 py-2 text-sm text-gray-700 flex items-center justify-start hover:bg-indigo-50",
                                      whiteLabelStyle?.theme?.hover?.bg
                                    )}
                                    onClick={menuItem?.onClick}
                                  >
                                    {menuItem.icon ?? null}
                                    {menuItem.label}
                                  </a>
                                )}
                              </Menu.Item>
                            ) : null;
                          })}

                          {isAuthenticated && (
                            <Menu.Item>
                              <a
                                className={classNames(
                                  "px-4 py-2 text-sm text-gray-700 flex items-center justify-start hover:bg-indigo-50",
                                  whiteLabelStyle?.theme?.hover?.bg
                                )}
                                onClick={() => {
                                  logout();
                                  resetIdentity();
                                }}
                              >
                                Logout
                              </a>
                            </Menu.Item>
                          )}
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                )}

                {/* Mobile Menu Button */}
                {isAuthenticated && (
                  <div
                    className="-mr-2 items-center"
                    style={{
                      display: isMobileScreen ? "flex" : "none"
                    }}
                  >
                    {/* Mobile menu Button */}
                    <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-white">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      ) : (
                        <Bars3Icon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden bg-gray-50 shadow-sm">
            {({ close }) => (
              <div className="border-t border-gray-200 pt-4 pb-3">
                {isAuthenticated && (
                  <div className="flex items-center px-4">
                    <div className="flex-shrink-0">
                      <div
                        className={classNames(
                          "h-8 w-8 md:h-10 md:w-10 text-sm font-semibold md:font-base md:text-sm rounded-full text-white bg-indigo-600 flex items-center justify-center",
                          whiteLabelStyle?.theme?.color?.darkbg
                        )}
                      >
                        {userState?.name && initializeName(userState.name)}
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-700">
                        {userState?.name}
                      </div>
                      <div className="text-xs font-medium text-gray-400">
                        {userState?.email}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-3 space-y-1">
                  {menuDropdown.map((menuItem, index) => {
                    const { href, onClick, icon, label, showInMobile } =
                      menuItem;
                    if (isMobileScreen && showInMobile) {
                      if (href) {
                        return (
                          <Disclosure.Button
                            key={index}
                            as={NavLink}
                            to={href!}
                            onClick={onClick! ?? null}
                            className="flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          >
                            {icon ?? null}
                            {label}
                          </Disclosure.Button>
                        );
                      } else {
                        return (
                          <a
                            key={index}
                            className="flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            onClick={(e) => {
                              onClick?.(e);
                              close();
                            }}
                          >
                            {label}
                          </a>
                        );
                      }
                    } else {
                      return null;
                    }
                  })}

                  {isAuthenticated && (
                    <a
                      className="flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      onClick={() => {
                        logout();
                        close();
                        resetIdentity();
                      }}
                    >
                      Logout
                    </a>
                  )}
                </div>
              </div>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
